import {
	Dispatch,
} from 'redux';
// Import services
import {
	StringService,
} from '@app/services';
// Import action type enumeration.
import ActionType from '@app/redux/actions/types';
import {
	NotificationType,
} from '@app/enumerations';
// Import api models.
import {
	PolicyNote,
	PolicyNoteDocumentWithData,
	PolicyNoteEmail,
	PolicyNoteRecord,
} from '@app/models/api';
// Import controller factory.
import ControllerFactoryProvider from '@app/controllers/factory';
// Import action methods.
import {
	addNotification,
	endProcessing,
	startProcessing,
} from '@app/redux/actions/methods';
// Import action factory methods.
import {
	loadPolicyNoteEmailsAction,
} from '@app/redux/actions/factories';
// Import actions.
import {
	LoadPolicyNoteEmailsAction,
	PolicyAction,
} from '@app/redux/actions';
// Import action parameters.
import {
	OverlayTargetParameters,
	PolicyParameters,
} from './ActionParametersBarrel';

interface PolicyNoteParameters extends PolicyParameters {
	policyNoteId: number;
}


// Load Policy Notes Action.

interface LoadPolicyNotesAction extends PolicyAction {
	policyNotes: PolicyNoteRecord[];
}

type LoadPolicyNotesParameters = PolicyParameters & OverlayTargetParameters;

const loadPolicyNotesAction: (policyNotes: PolicyNoteRecord[], params: LoadPolicyNotesParameters) =>
	LoadPolicyNotesAction = (policyNotes: PolicyNoteRecord[], params: LoadPolicyNotesParameters) => ({
		...params,
		policyNotes: policyNotes,
		type: ActionType.LOAD_POLICY_NOTES,
	});

const loadPolicyNotes = (params: LoadPolicyNotesParameters) =>
	async (dispatch: Dispatch): Promise<PolicyNoteRecord[]> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-loadPolicyNotes`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Loading notes...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to load policy notes.
			let policyNotes: PolicyNoteRecord[] = await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().getPolicyNotes(params.policyId);
			// Load policy notes to redux.
			dispatch<LoadPolicyNotesAction>(loadPolicyNotesAction(policyNotes, params));
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return policyNotes;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Load Policy Note Action.

interface LoadPolicyNoteAction extends PolicyAction {
	policyNote: PolicyNoteRecord;
}

type LoadPolicyNoteParameters = PolicyParameters;

const loadPolicyNoteAction: (policyNote: PolicyNoteRecord, params: PolicyParameters) =>
	LoadPolicyNoteAction = (policyNote: PolicyNoteRecord, params: PolicyParameters) => ({
		...params,
		policyNote: policyNote,
		type: ActionType.LOAD_POLICY_NOTE,
	});


// Add Policy Note Action.

interface AddPolicyNoteParameters extends PolicyParameters, OverlayTargetParameters {
	note: string;
	document: PolicyNoteDocumentWithData;
};

const addPolicyNote = (params: AddPolicyNoteParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-addPolicyNote`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Adding note...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to add policy note.
			await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().savePolicyNote(params.policyId, params.note, params.document);
			// Reload policy notes.
			let policyNotes: PolicyNoteRecord[] = await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().getPolicyNotes(params.policyId);
			// Reload policy note emails.
			let policyNoteEmails: PolicyNoteEmail[] = await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().getPolicyNoteEmails(params.policyId);
			// Load policy notes to redux.
			dispatch<LoadPolicyNotesAction>(loadPolicyNotesAction(policyNotes, params));
			// Load policy note emails to redux.
			dispatch<LoadPolicyNoteEmailsAction>(loadPolicyNoteEmailsAction(policyNoteEmails, params));
			// Add success notification.
			await addNotification({
				title: 'Success',
				primaryMessage: `Note was successfully added.`,
				notificationType: NotificationType.SUCCESS,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Add Policy Note Admin Action.

interface AddPolicyNoteAdminParameters extends PolicyParameters, OverlayTargetParameters {
	policyNote: PolicyNote;
	document: PolicyNoteDocumentWithData;
};

const addPolicyNoteAdmin = (params: AddPolicyNoteAdminParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-addPolicyNoteAdmin`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Adding note...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to add policy note as admin.
			await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().savePolicyNoteAdmin(params.policyId, params.policyNote, params.document);
			// Reload policy notes.
			let policyNotes: PolicyNoteRecord[] = await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().getPolicyNotes(params.policyId);
			// Reload policy note emails.
			let policyNoteEmails: PolicyNoteEmail[] = await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().getPolicyNoteEmails(params.policyId);
			// Load policy notes to redux.
			dispatch<LoadPolicyNotesAction>(loadPolicyNotesAction(policyNotes, params));
			// Load policy note emails to redux.
			dispatch<LoadPolicyNoteEmailsAction>(loadPolicyNoteEmailsAction(policyNoteEmails, params));
			// Add success notification.
			await addNotification({
				title: 'Success',
				primaryMessage: `Note was successfully added.`,
				notificationType: NotificationType.SUCCESS,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Add Note To Policy Note Action.

interface AddNoteToPolicyNoteParameters extends PolicyNoteParameters, OverlayTargetParameters {
	note: string;
};

const addNoteToPolicyNote = (params: AddNoteToPolicyNoteParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-addNoteToPolicyNote`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Adding additional note...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to add note to policy note.
			await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().saveNoteToPolicyNote(params.policyNoteId, params.note);
			// Reload policy notes.
			let policyNotes: PolicyNoteRecord[] = await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().getPolicyNotes(params.policyId);
			// Reload policy note emails.
			let policyNoteEmails: PolicyNoteEmail[] = await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().getPolicyNoteEmails(params.policyId);
			// Load policy note to redux.
			dispatch<LoadPolicyNotesAction>(loadPolicyNotesAction(policyNotes, params));
			// Load policy note emails to redux.
			dispatch<LoadPolicyNoteEmailsAction>(loadPolicyNoteEmailsAction(policyNoteEmails, params));
			// Add success notification.
			await addNotification({
				title: 'Success',
				primaryMessage: `Note was successfully added.`,
				notificationType: NotificationType.SUCCESS,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Change Policy Note Flag Date Action.

interface ChangePolicyNoteFlagDateParameters extends PolicyNoteParameters, OverlayTargetParameters {
	flagDate: Date;
	note: string;
};

const changePolicyNoteFlagDate = (params: ChangePolicyNoteFlagDateParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-changePolicyNoteFlagDate`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Updating flag date...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to update policy note flag date.
			await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().changeFlagDate(params.policyNoteId, params.flagDate, params.note);
			// Reload policy notes.
			let policyNotes: PolicyNoteRecord[] = await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().getPolicyNotes(params.policyId);
			// Reload policy note emails.
			let policyNoteEmails: PolicyNoteEmail[] = await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().getPolicyNoteEmails(params.policyId);
			// Load policy note to redux.
			dispatch<LoadPolicyNotesAction>(loadPolicyNotesAction(policyNotes, params));
			// Load policy note emails to redux.
			dispatch<LoadPolicyNoteEmailsAction>(loadPolicyNoteEmailsAction(policyNoteEmails, params));
			// Add success notification.
			await addNotification({
				title: 'Success',
				primaryMessage: `Flag date was successfully updated.`,
				notificationType: NotificationType.SUCCESS,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Mark Policy Note As Complete Action.

interface MarkPolicyNoteAsCompleteParameters extends PolicyNoteParameters, OverlayTargetParameters {
	note: string;
};

const markPolicyNoteAsComplete = (params: MarkPolicyNoteAsCompleteParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-markPolicyNoteAsComplete`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Marking note as complete...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to mark policy note as complete.
			await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().markPolicyNoteAsComplete(params.policyNoteId, params.note);
			// Reload policy notes.
			let policyNotes: PolicyNoteRecord[] = await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().getPolicyNotes(params.policyId);
			// Reload policy note emails.
			let policyNoteEmails: PolicyNoteEmail[] = await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().getPolicyNoteEmails(params.policyId);
			// Load policy note to redux.
			dispatch<LoadPolicyNotesAction>(loadPolicyNotesAction(policyNotes, params));
			// Load policy note emails to redux.
			dispatch<LoadPolicyNoteEmailsAction>(loadPolicyNoteEmailsAction(policyNoteEmails, params));
			// Add success notification.
			await addNotification({
				title: 'Success',
				primaryMessage: `Flag note was successfully resolved.`,
				notificationType: NotificationType.SUCCESS,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Mark Policy Note As Complete Action.

type ArchivePolicyNoteParameters = PolicyNoteParameters & OverlayTargetParameters;

const archivePolicyNote = (params: ArchivePolicyNoteParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-archivePolicyNote`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Archiving note...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to archive policy note.
			await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().archivePolicyNote(params.policyNoteId);
			// Call api to load policy notes.
			let policyNotes: PolicyNoteRecord[] = await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().getPolicyNotes(params.policyId);
			// Reload policy note emails.
			let policyNoteEmails: PolicyNoteEmail[] = await ControllerFactoryProvider.getControllerFactory().getPolicyNoteController().getPolicyNoteEmails(params.policyId);
			// Load policy note to redux.
			dispatch<LoadPolicyNotesAction>(loadPolicyNotesAction(policyNotes, params));
			// Load policy note emails to redux.
			dispatch<LoadPolicyNoteEmailsAction>(loadPolicyNoteEmailsAction(policyNoteEmails, params));
			// Add success notification.
			await addNotification({
				title: 'Success',
				primaryMessage: `Note was successfully archived.`,
				notificationType: NotificationType.SUCCESS,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Select Policy Note Action.

interface SelectPolicyNoteAction extends PolicyAction {
	policyNoteId: number;
}

type SelectPolicyNoteParameters = PolicyNoteParameters;

const selectPolicyNoteAction: (params: SelectPolicyNoteParameters) =>
	SelectPolicyNoteAction = (params: SelectPolicyNoteParameters) => ({
		...params,
		type: ActionType.SELECT_POLICY_NOTE,
	});

const selectPolicyNote = (params: SelectPolicyNoteParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		// Select policy note on redux.
		dispatch<SelectPolicyNoteAction>(selectPolicyNoteAction(params));
	};


// Unselect Policy Note Action.

type UnselectPolicyNoteAction = PolicyAction;

type UnselectPolicyNoteParameters = PolicyParameters;

const unselectPolicyNoteAction: (params: UnselectPolicyNoteParameters) =>
	UnselectPolicyNoteAction = (params: UnselectPolicyNoteParameters) => ({
		...params,
		type: ActionType.UNSELECT_POLICY_NOTE,
	});

const unselectPolicyNote = (params: UnselectPolicyNoteParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		// Select policy note on redux.
		dispatch<UnselectPolicyNoteAction>(unselectPolicyNoteAction(params));
	};


// Export actions.
export {
	LoadPolicyNotesAction,
	LoadPolicyNoteAction,
	SelectPolicyNoteAction,
	UnselectPolicyNoteAction,
};

// Export action methods.
export {
	loadPolicyNotes,
	addPolicyNote,
	addPolicyNoteAdmin,
	addNoteToPolicyNote,
	changePolicyNoteFlagDate,
	markPolicyNoteAsComplete,
	archivePolicyNote,
	selectPolicyNote,
	unselectPolicyNote,
};

// Export action parameters.
export {
	PolicyNoteParameters,
	LoadPolicyNotesParameters,
	LoadPolicyNoteParameters,
	AddPolicyNoteParameters,
	AddPolicyNoteAdminParameters,
	AddNoteToPolicyNoteParameters,
	ChangePolicyNoteFlagDateParameters,
	MarkPolicyNoteAsCompleteParameters,
	ArchivePolicyNoteParameters,
	SelectPolicyNoteParameters,
	UnselectPolicyNoteParameters,
};

// Export action factories.
export {
	loadPolicyNotesAction,
	loadPolicyNoteAction,
	selectPolicyNoteAction,
	unselectPolicyNoteAction,
};